import { z } from 'zod';
import { einrichtungHerkunftSchema, einrichtungZustandSchema, umsatzVorsystemQuelleSchema, } from './einstellungen.js';
import { centAmount, isoDate, nonNegativeAmount } from './posting.js';
/**
 * Die Verträge der Ersteinrichtung — Auftrag `PF-einrichtung`, Task 7 des
 * Plans `docs/superpowers/plans/2026-09-29-systemeinstellungen-umsetzung.md`,
 * Spec `docs/superpowers/specs/2026-09-29-systemeinstellungen-design.md`,
 * Abschnitt 6 (`D-9`, `D-46`) und 6.3 (Vorsystem-Umsatz, `E-1617`).
 *
 * **Eine eigene Datei und nicht `einstellungen.ts`**: Die Einrichtung hat
 * eigene Wege (Start mit Verwerfen, Kreise, Abschluss), und die Lesesicht der
 * Einstellungen bleibt, was sie ist.
 *
 * **Die offenen Punkte sind Schlüssel und keine Sätze.** Sie kommen aus
 * `einrichtung_abschlusspruefung()` (Migration `0260`) und werden in der
 * Oberfläche übersetzt: `journalbeginn_fehlt`, `grundbestand:<kennwert>`,
 * `kreis_unbestaetigt:<kreis>`, `aussteller_fehlt`,
 * `aussteller_pflichtangabe:<spalte>`, `aussteller_fremde_bankverbindung`,
 * `steuerprofil_fehlt`, `freigabeprofil_fehlt`.
 */
/** Die Form eines Kreiskennzeichens — dieselbe wie `number_range_prefix_shape` (`0020`, `0069`). */
export const kreisKennzeichenSchema = z
    .string()
    .regex(/^[A-Z]{0,3}-?$/, 'Höchstens drei Grossbuchstaben, danach wahlweise ein Bindestrich.');
/** Der Schlüssel eines Nummernkreises, wie `number_range.key` ihn führt. */
export const kreisSchluesselSchema = z
    .string()
    .regex(/^[a-z][a-z_]*$/, 'Kein Schlüssel eines Nummernkreises.');
/** Ein Belegkreis im Einrichtungsstand. */
export const einrichtungKreisSchema = z
    .object({
    /** `number_range.key`. */
    rangeKey: kreisSchluesselSchema,
    /** `number_range.purpose` — wofür der Kreis Nummern vergibt. */
    zweck: z.string(),
    /** Das heutige Kennzeichen; `''` ist eine Festlegung (Jahr und Zähler ohne Vorsatz). */
    kennzeichen: z.string().nullable(),
    /** Der Zählerstand — `bigint`, deshalb als Zeichenkette. Die nächste Nummer ist er plus eins. */
    zaehlerstand: z.string().regex(/^[0-9]+$/),
    /** Ob der Kreis in dieser Einrichtung bestätigt ist (`einrichtung_kreis`). */
    bestaetigt: z.boolean(),
})
    .strict();
/** Ein Umsatz aus einem Vorsystem — eine Zeile von `umsatz_vorsystem`. */
export const umsatzVorsystemSchema = z
    .object({
    jahr: z.number().int().min(2000).max(2999),
    quelle: umsatzVorsystemQuelleSchema,
    betrag: centAmount,
    erfasstAm: z.string().datetime(),
})
    .strict();
/**
 * Die Antwort von `GET /api/v1/einstellungen/einrichtung` — der Stand der
 * Ersteinrichtung samt der Punkte, die ihren Abschluss noch verhindern.
 */
export const einrichtungsstandSchema = z
    .object({
    herkunft: einrichtungHerkunftSchema,
    zustand: einrichtungZustandSchema,
    journalbeginn: isoDate.nullable(),
    /** Wann der Grundbestand verworfen wurde; `null`, solange nicht. */
    grundbestandVerworfenAm: z.string().datetime().nullable(),
    kreise: z.array(einrichtungKreisSchema),
    umsatzVorsystem: z.array(umsatzVorsystemSchema),
    /** Leer heisst: der Abschluss ist möglich. Nach dem Abschluss immer leer. */
    offenePunkte: z.array(z.string()),
})
    .strict();
/**
 * Der Rumpf von `PUT /api/v1/einstellungen/einrichtung/kreise/:rangeKey` —
 * Kennzeichen und erste Nummer eines Belegkreises (Schritt 5 des Assistenten).
 * Die Startnummer ist eine Zeichenkette aus Ziffern (der Kreis reicht bis
 * 99.999.999, und ein Zähler ist nie eine Fliesskommazahl).
 */
export const einrichtungKreisSetzenSchema = z
    .object({
    kennzeichen: kreisKennzeichenSchema,
    startnummer: z.string().regex(/^[1-9][0-9]{0,7}$/, 'Eine ganze Zahl von 1 bis 99999999.'),
})
    .strict();
/**
 * Der Rumpf von `PUT /api/v1/einstellungen/umsatz-vorsystem` und des
 * CLI-Läufers `einrichtung:umsatz-vorsystem` (`E-1617`). Der Betrag ist ein
 * Geldbetrag ab null mit höchstens zwei Nachkommastellen — nie eine Zahl.
 */
export const umsatzVorsystemSetzenSchema = z
    .object({
    jahr: z.number().int().min(2000).max(2999),
    quelle: umsatzVorsystemQuelleSchema,
    betrag: nonNegativeAmount,
})
    .strict();
//# sourceMappingURL=einrichtung.js.map